import React, {useRef, useEffect} from "react";
import * as maptilersdk from "@maptiler/sdk";
import "@maptiler/sdk/dist/maptiler-sdk.css";
import "./Map.css";
import routesData, {ferryRoutesGeoJSON} from "./routesDb";

const Map = ({
  selectedPortofLoading,
  selectedPortofDischarge,
  setSelectedPortofLoading,
  setSelectedPortofDischarge,
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]); // Speichere Marker-Referenzen
  const clickHandlers = useRef([]); // Speichere Click-Handler-Referenzen
  const hoverTimeouts = useRef({}); // Speichere Hover-Timeouts
  const homeCoordinates = {lat: 53.073635, lng: 8.806422};
  const zoomLevel = 5;
  maptilersdk.config.apiKey = "r5ep0WVbl3yLptR1sD2J";

  // Reset Funktion
  const handleReset = () => {
    console.log("Resetting map and selections");

    // Lösche ausgewählte Häfen
    setSelectedPortofLoading(null);
    setSelectedPortofDischarge(null);

    // Lösche alle Routen
    if (map.current && map.current.getSource("possible_routes")) {
      map.current.getSource("possible_routes").setData({
        type: "FeatureCollection",
        features: [],
      });
    }
    if (map.current && map.current.getSource("selected_route")) {
      map.current.getSource("selected_route").setData({
        type: "FeatureCollection",
        features: [],
      });
    }
    if (map.current && map.current.getSource("hover_routes")) {
      map.current.getSource("hover_routes").setData({
        type: "FeatureCollection",
        features: [],
      });
    }

    // Zentriere Karte auf Ausgangspunkt
    if (map.current) {
      map.current.flyTo({
        center: [homeCoordinates.lng, homeCoordinates.lat],
        zoom: zoomLevel,
        duration: 2000,
        essential: true,
      });
    }
  };

  // Hilfsfunktion zum Vergleichen von Koordinaten
  const coordinatesMatch = (lng1, lat1, lng2, lat2) => {
    return Math.abs(lng1 - lng2) < 0.0001 && Math.abs(lat1 - lat2) < 0.0001;
  };

  // Funktion zum Finden aller Routen eines Hafens
  const getRoutesForPort = (port) => {
    return ferryRoutesGeoJSON.features.filter((feature) => {
      const coordinates = feature.geometry.coordinates[0];
      const firstPoint = coordinates[0];
      const lastPoint = coordinates[coordinates.length - 1];

      const isStartPort = coordinatesMatch(
        firstPoint[0],
        firstPoint[1],
        port.lng,
        port.lat,
      );

      const isEndPort = coordinatesMatch(
        lastPoint[0],
        lastPoint[1],
        port.lng,
        port.lat,
      );

      return isStartPort || isEndPort;
    });
  };

  // Initialisierung der Karte
  useEffect(() => {
    if (map.current) return;

    map.current = new maptilersdk.Map({
      container: mapContainer.current,
      style:
        "https://api.maptiler.com/maps/0199a3db-7549-7feb-9cf3-308952b48b0b/style.json?key=aIwSDQXuBxM26xom1lWf",
      center: [homeCoordinates.lng, homeCoordinates.lat],
      zoom: zoomLevel,
    });

    // Füge Marker für jede Route hinzu
    routesData.forEach((route) => {
      // Erstelle ein Custom Marker Element
      const el = document.createElement("div");
      el.className = "custom-marker";
      el.style.backgroundImage = "url(img/ph_pin_port.png)";
      el.style.width = "20px";
      el.style.height = "20px";
      el.style.backgroundSize = "contain";
      el.style.backgroundRepeat = "no-repeat";
      el.style.cursor = "pointer";

      // Erstelle das Popup separat
      const popup = new maptilersdk.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: false,
        className: "port-hover-popup",
      }).setHTML(
        `<h6 style="margin: 5px; font-size: 14px;">${route.name}</h6>`,
      );

      // Erstelle den Marker mit Popup
      const marker = new maptilersdk.Marker({element: el})
        .setLngLat([route.lng, route.lat])
        .setPopup(popup)
        .addTo(map.current);

      // Speichere Marker mit Element und Route
      markers.current.push({
        marker: marker,
        element: el,
        route: route,
        popup: popup,
      });
    });

    // Event Handler für das Laden der Karte
    map.current.on("load", () => {
      console.log("Map loaded, adding sources and layers");

      // Füge eine leere GeoJSON Quelle für mögliche Routen hinzu (gestrichelt)
      map.current.addSource("possible_routes", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      // Füge eine leere GeoJSON Quelle für Hover-Routen hinzu
      map.current.addSource("hover_routes", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      // Füge eine leere GeoJSON Quelle für die ausgewählte Route hinzu (durchgehend rot)
      map.current.addSource("selected_route", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      // Layer für mögliche Routen (gestrichelt, grau)
      map.current.addLayer({
        id: "possible_routes_layer",
        type: "line",
        source: "possible_routes",
        layout: {},
        paint: {
          "line-color": "#616064ff",
          "line-width": 3,
          "line-dasharray": [2, 3],
        },
      });

      // Layer für Hover-Routen (gestrichelt, grau - gleich wie possible_routes)
      map.current.addLayer({
        id: "hover_routes_layer",
        type: "line",
        source: "hover_routes",
        layout: {},
        paint: {
          "line-color": "#616064ff", // Grau (gleich wie possible_routes)
          "line-width": 3,
          "line-dasharray": [2, 3], // Gestrichelt
          "line-opacity": 0.8,
        },
      });

      // Layer für ausgewählte Route (durchgehend, rot)
      map.current.addLayer({
        id: "selected_route_layer",
        type: "line",
        source: "selected_route",
        layout: {},
        paint: {
          "line-color": "#FF0000", // Rot
          "line-width": 4, // Stärke 4
        },
      });

      console.log("Sources and layers added");
    });
  }, [homeCoordinates.lng, homeCoordinates.lat, zoomLevel]);

  // Hover Event Listener - separat registriert um selectedPortofLoading zu checken
  useEffect(() => {
    console.log("Registering hover handlers");

    markers.current.forEach(({element, marker, route, popup}) => {
      // Entferne alte Hover-Handler
      const oldEnter = element._hoverEnter;
      const oldLeave = element._hoverLeave;
      if (oldEnter) element.removeEventListener("mouseenter", oldEnter);
      if (oldLeave) element.removeEventListener("mouseleave", oldLeave);

      // Neuer mouseenter Handler
      const handleMouseEnter = () => {
        // Zeige Popup IMMER
        if (!popup.isOpen()) {
          marker.togglePopup();
        }

        // Zeige Routen NUR wenn KEIN Ladehafen ausgewählt ist
        if (
          !selectedPortofLoading &&
          map.current &&
          map.current.getSource("hover_routes")
        ) {
          // Kurze Verzögerung für bessere UX
          hoverTimeouts.current[route.name] = setTimeout(() => {
            const portRoutes = getRoutesForPort(route);
            console.log(`Hover routes for ${route.name}:`, portRoutes.length);

            map.current.getSource("hover_routes").setData({
              type: "FeatureCollection",
              features: portRoutes,
            });
          }, 150); // 150ms Verzögerung
        }
      };

      // Neuer mouseleave Handler
      const handleMouseLeave = () => {
        // Verstecke Popup
        if (popup.isOpen()) {
          marker.togglePopup();
        }

        // Timeout abbrechen falls vorhanden
        if (hoverTimeouts.current[route.name]) {
          clearTimeout(hoverTimeouts.current[route.name]);
          delete hoverTimeouts.current[route.name];
        }

        // Lösche Hover-Routen
        if (map.current && map.current.getSource("hover_routes")) {
          map.current.getSource("hover_routes").setData({
            type: "FeatureCollection",
            features: [],
          });
        }
      };

      // Speichere Handler-Referenzen
      element._hoverEnter = handleMouseEnter;
      element._hoverLeave = handleMouseLeave;

      // Registriere Handler
      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);
    });

    // Cleanup
    return () => {
      // Räume alle Timeouts auf
      Object.values(hoverTimeouts.current).forEach((timeout) =>
        clearTimeout(timeout),
      );
      hoverTimeouts.current = {};
    };
  }, [selectedPortofLoading, selectedPortofDischarge]); // Auch selectedPortofDischarge beachten

  // Event Listener für Marker-Klicks
  useEffect(() => {
    console.log(
      "Registering marker click handlers, selectedPortofLoading:",
      selectedPortofLoading,
      "selectedPortofDischarge:",
      selectedPortofDischarge,
    );

    // Entferne alle vorherigen Event Listener
    clickHandlers.current.forEach(({element, handler}) => {
      element.removeEventListener("click", handler);
    });
    clickHandlers.current = [];

    // Füge neue Event Listener hinzu
    markers.current.forEach(({element, marker, route}) => {
      const handler = (e) => {
        e.stopPropagation();

        console.log("Marker clicked:", route.name);
        console.log("Current selectedPortofLoading:", selectedPortofLoading);
        console.log(
          "Current selectedPortofDischarge:",
          selectedPortofDischarge,
        );

        // Fall 1: Beide Häfen ausgewählt -> Reset und neuer Starthafen
        if (selectedPortofLoading && selectedPortofDischarge) {
          console.log(
            "Both ports selected, resetting and setting new loading port:",
            route.name,
          );
          setSelectedPortofLoading(route);
          setSelectedPortofDischarge(null);
        }
        // Fall 2: Kein Ladehafen ausgewählt -> Setze als Ladehafen
        else if (!selectedPortofLoading) {
          console.log("Setting as loading port:", route.name);
          setSelectedPortofLoading(route);
          setSelectedPortofDischarge(null);
        }
        // Fall 3: Ladehafen ausgewählt -> Prüfe ob Route existiert
        else {
          // Prüfe ob eine Route zwischen selectedPortofLoading und dem geklickten Hafen existiert
          const routeExists = ferryRoutesGeoJSON.features.some((feature) => {
            const coordinates = feature.geometry.coordinates[0];
            const firstPoint = coordinates[0];
            const lastPoint = coordinates[coordinates.length - 1];

            // Prüfe beide Richtungen
            const forwardMatch =
              coordinatesMatch(
                firstPoint[0],
                firstPoint[1],
                selectedPortofLoading.lng,
                selectedPortofLoading.lat,
              ) &&
              coordinatesMatch(
                lastPoint[0],
                lastPoint[1],
                route.lng,
                route.lat,
              );

            const reverseMatch =
              coordinatesMatch(
                firstPoint[0],
                firstPoint[1],
                route.lng,
                route.lat,
              ) &&
              coordinatesMatch(
                lastPoint[0],
                lastPoint[1],
                selectedPortofLoading.lng,
                selectedPortofLoading.lat,
              );

            return forwardMatch || reverseMatch;
          });

          if (routeExists) {
            // Route existiert -> Setze als Entladehafen
            console.log("Route exists, setting as discharge port:", route.name);
            setSelectedPortofDischarge(route);
          } else {
            // Keine Route -> Setze als neuen Starthafen
            console.log(
              "No route exists, setting as new loading port:",
              route.name,
            );
            setSelectedPortofLoading(route);
            setSelectedPortofDischarge(null);
          }
        }
      };

      element.addEventListener("click", handler);

      // Speichere Handler-Referenz für späteres Entfernen
      clickHandlers.current.push({element, handler});
    });

    // Cleanup function
    return () => {
      console.log("Cleaning up marker event listeners");
      clickHandlers.current.forEach(({element, handler}) => {
        element.removeEventListener("click", handler);
      });
    };
  }, [
    selectedPortofLoading,
    selectedPortofDischarge,
    setSelectedPortofLoading,
    setSelectedPortofDischarge,
  ]);

  // Zeige Routen basierend auf Auswahl
  useEffect(() => {
    console.log("Port selection changed");
    console.log("Loading:", selectedPortofLoading);
    console.log("Discharge:", selectedPortofDischarge);

    if (!map.current) {
      console.log("Map not initialized yet");
      return;
    }

    if (!map.current.isStyleLoaded()) {
      console.log("Waiting for map to load...");
      map.current.once("load", updateRoutes);
      return;
    }

    updateRoutes();

    function updateRoutes() {
      // Prüfe ob die Quellen existieren
      if (
        !map.current.getSource("possible_routes") ||
        !map.current.getSource("selected_route") ||
        !map.current.getSource("hover_routes")
      ) {
        console.log("Sources do not exist yet");
        return;
      }

      // Lösche Hover-Routen wenn Port ausgewählt wird
      map.current.getSource("hover_routes").setData({
        type: "FeatureCollection",
        features: [],
      });

      // Fall 1: Beide Häfen ausgewählt - zeige nur die ausgewählte Route
      if (selectedPortofLoading && selectedPortofDischarge) {
        console.log("Both ports selected, showing selected route");

        // Finde die Route zwischen den beiden Häfen
        const selectedRoute = ferryRoutesGeoJSON.features.find((feature) => {
          const coordinates = feature.geometry.coordinates[0];
          const firstPoint = coordinates[0];
          const lastPoint = coordinates[coordinates.length - 1];

          // Prüfe beide Richtungen
          const forwardMatch =
            coordinatesMatch(
              firstPoint[0],
              firstPoint[1],
              selectedPortofLoading.lng,
              selectedPortofLoading.lat,
            ) &&
            coordinatesMatch(
              lastPoint[0],
              lastPoint[1],
              selectedPortofDischarge.lng,
              selectedPortofDischarge.lat,
            );

          const reverseMatch =
            coordinatesMatch(
              firstPoint[0],
              firstPoint[1],
              selectedPortofDischarge.lng,
              selectedPortofDischarge.lat,
            ) &&
            coordinatesMatch(
              lastPoint[0],
              lastPoint[1],
              selectedPortofLoading.lng,
              selectedPortofLoading.lat,
            );

          return forwardMatch || reverseMatch;
        });

        if (selectedRoute) {
          console.log("Selected route found:", selectedRoute.properties?.name);

          // Zeige die ausgewählte Route (rot, durchgehend)
          map.current.getSource("selected_route").setData({
            type: "FeatureCollection",
            features: [selectedRoute],
          });

          // Lösche mögliche Routen
          map.current.getSource("possible_routes").setData({
            type: "FeatureCollection",
            features: [],
          });

          // Zoom zur ausgewählten Route
          const bounds = new maptilersdk.LngLatBounds();
          selectedRoute.geometry.coordinates[0].forEach((coord) => {
            bounds.extend(coord);
          });

          map.current.fitBounds(bounds, {
            padding: {top: 200, bottom: 200, left: 200, right: 200},
            duration: 2000,
            essential: true,
          });
        } else {
          console.log("No direct route found between selected ports");
          // Keine direkte Route gefunden - lösche alle
          map.current.getSource("selected_route").setData({
            type: "FeatureCollection",
            features: [],
          });
          map.current.getSource("possible_routes").setData({
            type: "FeatureCollection",
            features: [],
          });
        }
      }
      // Fall 2: Nur Ladehafen ausgewählt - zeige mögliche Routen
      else if (selectedPortofLoading && !selectedPortofDischarge) {
        console.log("Only loading port selected, showing possible routes");

        // Finde alle möglichen Routen
        const possibleRoutes = getRoutesForPort(selectedPortofLoading);

        console.log("Found possible routes:", possibleRoutes.length);

        // Zeige mögliche Routen (grau, gestrichelt)
        map.current.getSource("possible_routes").setData({
          type: "FeatureCollection",
          features: possibleRoutes,
        });

        // Lösche ausgewählte Route
        map.current.getSource("selected_route").setData({
          type: "FeatureCollection",
          features: [],
        });

        // Zoom zu allen möglichen Routen
        if (possibleRoutes.length > 0) {
          const bounds = new maptilersdk.LngLatBounds();
          possibleRoutes.forEach((route) => {
            route.geometry.coordinates[0].forEach((coord) => {
              bounds.extend(coord);
            });
          });

          map.current.fitBounds(bounds, {
            padding: {top: 200, bottom: 200, left: 200, right: 200},
            duration: 2000,
            essential: true,
          });
        } else {
          // Zoom zum Hafen wenn keine Routen gefunden
          map.current.flyTo({
            center: [selectedPortofLoading.lng, selectedPortofLoading.lat],
            zoom: 8,
            duration: 2000,
            essential: true,
          });
        }
      }
      // Fall 3: Keine Häfen ausgewählt - lösche alle Routen
      else {
        console.log("No ports selected, clearing all routes");
        map.current.getSource("possible_routes").setData({
          type: "FeatureCollection",
          features: [],
        });
        map.current.getSource("selected_route").setData({
          type: "FeatureCollection",
          features: [],
        });
      }
    }
  }, [selectedPortofLoading, selectedPortofDischarge]);

  return (
    <div style={{position: "relative", width: "100%", height: "100%"}}>
      <div ref={mapContainer} className="map" />

      {/* Reset Button - nur anzeigen wenn Häfen ausgewählt sind */}
      {(selectedPortofLoading || selectedPortofDischarge) && (
        <button
          onClick={handleReset}
          className="map-reset-button"
          title="Auswahl zurücksetzen"
        >
          ↺ Zurücksetzen
        </button>
      )}
    </div>
  );
};

export default Map;
